import { loadJs } from "../lib/loadJs";
import EventEmitter from "./EventEmitter";

export type YouTubePlayerOptions = {
  height: string | number,
  width: string | number,

}
const YOUTUBE_IFRAME_API_SRC = 'https://www.youtube.com/iframe_api'

const loadIframeAPICallbacks: any[] = []

const YOUTUBE_STATES = {
  '-1': 'unstarted',
  0: 'ended',
  1: 'playing',
  2: 'paused',
  3: 'buffering',
  5: 'cued'
}

export default class YouTubePlayer extends EventEmitter {
  private _id: string | null;
  private _opts: YouTubePlayerOptions | null;
  private _queue: [keyof YouTubePlayer, any[] | undefined][] = [];
  private _autoplay: boolean;
  private _player?: any;
  private _ready: boolean;
  private _api: any;
  private _start: number = 0;
  private _videoUrl?: string;
  private destroyed: boolean = false;

  constructor(element: HTMLElement, opts: YouTubePlayerOptions) {
    super()
    const elem = typeof element === 'string'
      ? document.querySelector(element)!
      : element

    if (elem.id) {
      this._id = elem.id // use existing element id
    } else {
      this._id = elem.id = 'ytplayer-' + Math.random().toString(16).slice(2, 8)
    }

    this._opts = Object.assign({
      height: '495',
      width: '880',
    }, opts || {});

    console.log('this._opts', this._opts)

    this._api = null
    this._autoplay = false // 自动播放
    this._player = null
    this._ready = false // is player ready?
    this._queue = []


    this._loadIframeAPI((err: any, api: any) => {
      if (err) return this._destroy(new Error('YouTube Iframe API failed to load'))
      this._api = api

      if (this._videoUrl) this.load(this._videoUrl, this._autoplay, this._start)
    })
  }
  _onReady () {
    if (this.destroyed) return

    this._ready = true

    this.load(this._videoUrl!, this._autoplay, this._start)

    this.emit('ready')

    this._flushQueue()
  }
  _onStateChange (data: any) {
    if (this.destroyed) return

    const state = YOUTUBE_STATES[data.data as keyof typeof YOUTUBE_STATES]
    if (state) {
      this.emit(state);
    }
  }
  _onError (data: any) {
    if (this.destroyed) return
  }
  //加载视频
  load (videoUrl: string, autoplay = false, start = 0) {
    this._videoUrl = videoUrl;
    this._autoplay = autoplay
    this._start = start

    if (!this._api) return

    if (!this._player) {
      this._createPlayer(videoUrl)
      return
    }

    if (!this._ready) return

    if (autoplay) {
      this._player.loadVideoByUrl(videoUrl, start)
    } else {
      this._player.cueVideoByUrl(videoUrl, start)
    }
  }
  _loadIframeAPI (cb: Function) {
    if ((window as any).YT && typeof (window as any).YT.Player === 'function') {
      return cb(null, (window as any).YT)
    }

    loadIframeAPICallbacks.push(cb)

    const scripts = Array.from(document.getElementsByTagName('script'))
    const isLoading = scripts.some(script => script.src === YOUTUBE_IFRAME_API_SRC)
    //如果之前没加载过，那就加载一次
    if (!isLoading) {

      loadJs(YOUTUBE_IFRAME_API_SRC).catch((err) => {
        while (loadIframeAPICallbacks.length) {
          const loadCb = loadIframeAPICallbacks.shift()!
          loadCb(err)
        }
      })
    }

    //如果其他地方已经定义过youtube的api，那就包装一次
    const prevOnYouTubeIframeAPIReady = (window as any).onYouTubeIframeAPIReady;
    (window as any).onYouTubeIframeAPIReady = () => {
      if (typeof prevOnYouTubeIframeAPIReady === 'function') {
        prevOnYouTubeIframeAPIReady()
      }
      while (loadIframeAPICallbacks.length) {
        const loadCb = loadIframeAPICallbacks.shift()
        loadCb(null, (window as any).YT)
      }
    };

  }
  //创建播放器
  _createPlayer (videoUrl: string) {
    if (this.destroyed) return
    const opts = this._opts

    this._player = new this._api.Player(this._id, {
      width: opts!.width,
      height: opts!.height,
      events: {
        onReady: () => this._onReady(),
        onStateChange: (data: keyof typeof YOUTUBE_STATES) => this._onStateChange(data),
        onError: (data: any) => this._onError(data)
      }
    });
  }
  play () {
    if (this._ready) this._player.playVideo()
    else this._queueCommand('play')
  } pause () {
    if (this._ready) this._player.pauseVideo()
    else this._queueCommand('pause')
  }

  stop () {
    if (this._ready) this._player.stopVideo()
    else this._queueCommand('stop')
  }

  seek (seconds: number) {
    if (this._ready) this._player.seekTo(seconds, true)
    else this._queueCommand('seek', seconds)
  }
  //加入到队列里
  _queueCommand (command: keyof YouTubePlayer, ...args: any[]) {
    if (this.destroyed) return
    this._queue.push([command, args])
  }
  _flushQueue () {
    while (this._queue.length) {
      const command = this._queue.shift()
      if (command) {
        // @ts-ignore
        this[command[0]].apply(this, command[1])
      }
    }
  }
  destroy () {
    this._destroy()
  }
  _destroy (err?: any) {
    if (this.destroyed) return
    this.destroyed = true

    if (this._player) {
      this._player.stopVideo && this._player.stopVideo()
      this._player.destroy()
    }

    this._videoUrl = undefined
    this._id = null
    this._opts = null
    this._api = null
    this._player = null
    this._ready = false
    this._queue.splice(0)
  }
}